import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  LinearProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  School as SchoolIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as AttachMoneyIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as InactiveIcon
} from '@mui/icons-material';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { schoolYearService } from '../../services/schoolYearService';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const SchoolYearDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [schoolYear, setSchoolYear] = useState<any>(null);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [yearRes, summaryRes] = await Promise.all([
        schoolYearService.getById(parseInt(id!)),
        schoolYearService.getSummary(parseInt(id!))
      ]);
      
      setSchoolYear(yearRes.data);
      setSummary(summaryRes.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const getMonthName = (monthNumber: number) => {
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return months[monthNumber - 1];
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !schoolYear) {
    return (
      <Alert severity="error">
        {error || 'Curso no encontrado'}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <IconButton component={Link} to="/school-years" sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Avatar sx={{ width: 56, height: 56, mr: 2, bgcolor: 'primary.main' }}>
            <SchoolIcon fontSize="large" />
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1">
              {schoolYear.name}
            </Typography>
            <Box display="flex" alignItems="center" mt={0.5}>
              <CalendarIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {getMonthName(schoolYear.start_month)} {schoolYear.start_year} - {getMonthName(schoolYear.end_month)} {schoolYear.end_year}
              </Typography>
            </Box>
          </Box>
          <Chip
            icon={schoolYear.is_active ? <CheckCircleIcon /> : <InactiveIcon />}
            label={schoolYear.is_active ? 'Activo' : 'Inactivo'}
            color={schoolYear.is_active ? 'success' : 'default'}
            sx={{ ml: 2 }}
          />
        </Box>
        
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/school-years/${id}/edit`)}
          disabled={!['admin'].includes(user?.role || '')}
        >
          Editar
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Ingresos Totales
              </Typography>
              <Typography variant="h4" color="success.main">
                ₽{schoolYear.stats?.total_income?.toLocaleString() || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                En {schoolYear.stats?.total_months || 0} meses
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Gastos Totales
              </Typography>
              <Typography variant="h4" color="error.main">
                ₽{schoolYear.stats?.total_expenses?.toLocaleString() || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Balance Neto
              </Typography>
              <Typography 
                variant="h4" 
                color={schoolYear.stats?.balance >= 0 ? 'success.main' : 'error.main'}
              >
                ₽{schoolYear.stats?.balance?.toLocaleString() || 0}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={75} 
                color={schoolYear.stats?.balance >= 0 ? 'success' : 'error'}
                sx={{ mt: 1, height: 6, borderRadius: 3 }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Resumen Mensual" />
          <Tab label="Gráficos" />
          <Tab label="Estadísticas" />
        </Tabs>
      </Paper>

      {/* Tab: Resumen Mensual */}
      <TabPanel value={tabValue} index={0}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Mes</TableCell>
                <TableCell align="right">Estudiantes</TableCell>
                <TableCell align="right">Ingresos</TableCell>
                <TableCell align="right">Pagos Profesores</TableCell>
                <TableCell align="right">Otros Gastos</TableCell>
                <TableCell align="right">Total Gastos</TableCell>
                <TableCell align="right">Balance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summary?.months?.map((month: any) => (
                <TableRow key={`${month.year}-${month.month}`}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {getMonthName(month.month_number)} {month.year}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{month.paid_count}</TableCell>
                  <TableCell align="right" sx={{ color: 'success.main' }}>
                    ₽{month.total_income.toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'warning.main' }}>
                    ₽{month.teacher_payments.toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'error.main' }}>
                    ₽{month.other_expenses.toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'error.main' }}>
                    ₽{month.total_expenses.toLocaleString()}
                  </TableCell>
                  <TableCell 
                    align="right"
                    sx={{ 
                      color: month.balance >= 0 ? 'success.main' : 'error.main',
                      fontWeight: 'bold'
                    }}
                  >
                    ₽{month.balance.toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Tab: Gráficos */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Ingresos vs Gastos por Mes
                </Typography>
                <Box height={400}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={summary?.months || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="month" 
                        tickFormatter={(value, index) => {
                          const month = summary?.months[index];
                          return month ? getMonthName(month.month_number).substring(0, 3) : '';
                        }}
                      />
                      <YAxis />
                      <Tooltip
                        formatter={(value, name, item, index, payload) => {
                            // value can be number | undefined
                            if (value === undefined || value === null) {
                            return '—'; // or '' or 'N/A' or 0 — choose what makes sense for your chart
                            }
                            return `₽${Number(value).toLocaleString('ru-RU')}`; // 'ru-RU' gives nice thousand separators
                        }}
                        labelFormatter={(label, payload) => {
                            if (payload && payload.length > 0) {
                            const data = payload[0].payload;
                            return `${getMonthName(data.month_number)} ${data.year}`;
                            }
                            return label; // fallback — usually won't be reached
                        }}
                      />
                      <Legend />
                      <Bar dataKey="total_income" fill="#4caf50" name="Ingresos" />
                      <Bar dataKey="total_expenses" fill="#f44336" name="Gastos" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Evolución del Balance
                </Typography>
                <Box height={400}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={summary?.months || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="month"
                        tickFormatter={(value, index) => {
                          const month = summary?.months[index];
                          return month ? getMonthName(month.month_number).substring(0, 3) : '';
                        }}
                      />
                      <YAxis />
                      <Tooltip
                        formatter={(value, name, item, index, payload) => {
                            // value can be number | undefined
                            if (value === undefined || value === null) {
                            return '—'; // or '' or 'N/A' or 0 — choose what makes sense for your chart
                            }
                            return `₽${Number(value).toLocaleString('ru-RU')}`; // 'ru-RU' gives nice thousand separators
                        }}
                        labelFormatter={(label, payload) => {
                            if (payload && payload.length > 0) {
                            const data = payload[0].payload;
                            return `${getMonthName(data.month_number)} ${data.year}`;
                            }
                            return label; // fallback — usually won't be reached
                        }}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="balance" 
                        stroke="#2196f3" 
                        name="Balance"
                        strokeWidth={3}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Tab: Estadísticas */}
      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Promedios del Curso
                </Typography>
                <TableContainer>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell>Ingreso Promedio Mensual</TableCell>
                        <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                          ₽{schoolYear.stats?.average_monthly_income?.toLocaleString() || 0}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Gasto Promedio Mensual</TableCell>
                        <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                          ₽{schoolYear.stats?.average_monthly_expenses?.toLocaleString() || 0}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Total Estudiantes (pagaron)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {schoolYear.stats?.total_students || 0}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Meses del Curso</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {schoolYear.stats?.total_months || 0}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Información Adicional
                </Typography>
                {schoolYear.description ? (
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body1">
                      {schoolYear.description}
                    </Typography>
                  </Paper>
                ) : (
                  <Alert severity="info">
                    No hay descripción para este curso
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
};

export default SchoolYearDetail;